import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { BarChart2 } from "lucide-react";
import { useGetNecApplicationsByCategoryQuery } from "../api/probationerApplicationApi";
import ApplicationAnalysisReport from "../components/ApplicationAnalysisReport";

type AdminApplication = {
  id: string;
  user_id?: number;
  logbook_diet_id?: string;
  application_category_id?: number;

  surname?: string;
  other_names?: string;
  title?: string;

  status?: string;
  completion_step?: number;
  created_at?: string;

  user?: { id?: number; name?: string; email?: string };
  category?: { id?: number; name?: string };

  diet?: {
    id: string;
    title?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    is_active?: number | boolean;
    created_at?: string;
  };
};

type NavState = {
  categoryName?: string;
  categoryId?: number;
  dietId?: string;
  dietTitle?: string;
};

function safeMsg(x: any) {
  return String(
    x?.data?.message ||
      x?.message ||
      x?.error ||
      x?.data?.error ||
      "Something went wrong"
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
}

/** ✅ SAME COLOR SYSTEM AS badges + tabs */
function StatusBadge({ status }: { status: string }) {
  const s = String(status || "pending").toLowerCase();

  // ✅ only highlight the 3 you care about; fallback is neutral gray
  const cls =
    s === "approved"
      ? "bg-green-100 text-green-700 border-green-200"
      : s === "rejected"
      ? "bg-red-100 text-red-700 border-red-200"
      : s === "submitted"
      ? "bg-sky-100 text-sky-700 border-sky-200"
      : "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <span
      className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${cls}`}
      title={s}
    >
      {s}
    </span>
  );
}

/**
 * ✅ Tabs: ONLY Approved, Submitted, Rejected (+ optional All)
 */
const TABS = [
  { key: "approved", label: "Accepted" },
  { key: "submitted", label: "Submitted" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" }, // remove this if you truly want only 3 tabs
] as const;

type TabKey = (typeof TABS)[number]["key"];

type NormalizedStatus = "approved" | "submitted" | "rejected" | "other";

function normalizeStatus(a: AdminApplication): NormalizedStatus {
  const st = String(a.status || "").toLowerCase();

  if (st === "approved") return "approved";
  if (st === "submitted") return "submitted";
  if (st === "rejected") return "rejected";

  return "other";
}

/** ✅ Color styles updated for only 3 tabs (+ all) */
function tabStyles(tab: TabKey, active: boolean) {
  const base =
    tab === "approved"
      ? "border-green-200"
      : tab === "submitted"
      ? "border-sky-200"
      : tab === "rejected"
      ? "border-red-200"
      : "border-gray-200";

  const activeBg =
    tab === "approved"
      ? "bg-green-600 text-white border-green-600"
      : tab === "submitted"
      ? "bg-sky-600 text-white border-sky-600"
      : tab === "rejected"
      ? "bg-red-600 text-white border-red-600"
      : "bg-black text-white border-black";

  const inactiveBg =
    tab === "approved"
      ? "bg-green-50 text-green-800 hover:bg-green-100"
      : tab === "submitted"
      ? "bg-sky-50 text-sky-800 hover:bg-sky-100"
      : tab === "rejected"
      ? "bg-red-50 text-red-800 hover:bg-red-100"
      : "bg-white text-gray-800 hover:bg-gray-50";

  return active
    ? ["border", "rounded-md", activeBg].join(" ")
    : ["border", "rounded-md", base, inactiveBg].join(" ");
}

function CountPill({ tab, count }: { tab: TabKey; count: number }) {
  const cls =
    tab === "approved"
      ? "bg-green-100 text-green-800 border-green-200"
      : tab === "submitted"
      ? "bg-sky-100 text-sky-800 border-sky-200"
      : tab === "rejected"
      ? "bg-red-100 text-red-800 border-red-200"
      : "bg-gray-100 text-gray-800 border-gray-200";

  return (
    <span
      className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${cls}`}
    >
      {count}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "approved" | "submitted" | "rejected" | "all";
}) {
  const cls =
    tone === "approved"
      ? "border-green-200 bg-green-50"
      : tone === "submitted"
      ? "border-sky-200 bg-sky-50"
      : tone === "rejected"
      ? "border-red-200 bg-red-50"
      : "border-gray-200 bg-gray-50";

  const numCls =
    tone === "approved"
      ? "text-green-700"
      : tone === "submitted"
      ? "text-sky-700"
      : tone === "rejected"
      ? "text-red-700"
      : "text-gray-800";

  return (
    <div className={`border rounded-xl p-3 ${cls}`}>
      <div className="text-xs text-gray-600">{label}</div>
      <div className={`text-xl font-bold ${numCls}`}>{value}</div>
    </div>
  );
}

function dietLabel(a: AdminApplication) {
  const d = a.diet;
  if (d?.title) return d.title;
  if (d?.id) return `Diet ${String(d.id).slice(0, 8)}…`;
  if (a.logbook_diet_id)
    return `Diet ${String(a.logbook_diet_id).slice(0, 8)}…`;
  return "Unknown Diet";
}

function dietMeta(a: AdminApplication) {
  const d = a.diet;
  const start = d?.start_date ? fmtDate(d.start_date) : "—";
  const end = d?.end_date ? fmtDate(d.end_date) : "—";
  const st = d?.status ? String(d.status) : "";
  return `${start} – ${end}${st ? ` • ${st}` : ""}`;
}

function CardRow({ a }: { a: AdminApplication }) {
  const fullName = [a.title, a.surname, a.other_names]
    .filter(Boolean)
    .join(" ");
  const userLine = [a.user?.name, a.user?.email].filter(Boolean).join(" • ");
  const st = String(a.status || "submitted").toLowerCase();

  return (
    <div className="border rounded-xl p-4 bg-white">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold">{fullName || a.id}</div>
            <StatusBadge status={st} />
          </div>

          {userLine && <div className="text-sm text-gray-600">{userLine}</div>}

          <div className="text-sm text-gray-600">
            Step: <span className="font-medium">{a.completion_step ?? 0}</span>{" "}
            • Created:{" "}
            <span className="font-medium">{fmtDate(a.created_at)}</span>
          </div>

          <div className="text-xs text-gray-500 break-all">ID: {a.id}</div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/probationer-applications/${a.id}`}
            className="px-3 py-2 rounded-md border text-sm"
          >
            View
          </Link>
        </div>
      </div>
    </div>
  );
}

function DietSection({
  title,
  meta,
  count,
  children,
}: {
  title: string;
  meta: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="border rounded-xl bg-white">
      <div className="p-4 border-b">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold">{title}</div>
            <div className="text-xs text-gray-600 mt-1">{meta}</div>
          </div>
          <span className="inline-flex items-center px-2 py-1 text-xs border rounded-full bg-gray-50 text-gray-800 border-gray-200">
            {count} application{count === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

export default function NecProbationerApplicationDietCategoryPage() {
  const navigate = useNavigate();

  const { dietId, categoryId } = useParams<{
    dietId: string;
    categoryId: string;
  }>();
  const cid = Number(categoryId);

  const location = useLocation();
  const navState = (location.state || {}) as NavState;

  const [tab, setTab] = useState<TabKey>("approved");

  const { data, isLoading, isFetching, error, refetch } =
    useGetNecApplicationsByCategoryQuery(
      { dietId: dietId!, categoryId: cid },
      { skip: !dietId || !cid }
    );

  const busy = isLoading || isFetching;

  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const list = useMemo(() => {
    const d: any = data;
    if (!d) return [] as AdminApplication[];
    if (Array.isArray(d)) return d as AdminApplication[];
    if (Array.isArray(d.data)) return d.data as AdminApplication[];
    return [] as AdminApplication[];
  }, [data]);

  const categoryName = useMemo(() => {
    const fromState = navState?.categoryName?.trim();
    if (fromState) return fromState;

    const fromApi = list[0]?.category?.name?.trim();
    if (fromApi) return fromApi;

    return cid ? `Category #${cid}` : "Category";
  }, [navState?.categoryName, list, cid]);

  const dietTitle = useMemo(() => {
    const fromState = navState?.dietTitle?.trim();
    if (fromState) return fromState;

    const fromApi = list[0]?.diet?.title?.trim();
    if (fromApi) return fromApi;

    return dietId ? `Diet ${String(dietId).slice(0, 8)}…` : "Diet";
  }, [navState?.dietTitle, list, dietId]);

  /** ✅ counts: only approved/submitted/rejected (+ all) */
  const counts = useMemo(() => {
    const c = {
      approved: 0,
      submitted: 0,
      rejected: 0,
      all: list.length,
    };

    for (const a of list) {
      const s = normalizeStatus(a);
      if (s === "approved") c.approved++;
      if (s === "submitted") c.submitted++;
      if (s === "rejected") c.rejected++;
    }

    return c;
  }, [list]);

  /** ✅ visible: only the selected 3 tabs (+ all) */
  const visible = useMemo(() => {
    if (tab === "all") return list;
    if (tab === "approved")
      return list.filter((a) => normalizeStatus(a) === "approved");
    if (tab === "submitted")
      return list.filter((a) => normalizeStatus(a) === "submitted");
    if (tab === "rejected")
      return list.filter((a) => normalizeStatus(a) === "rejected");
    return list;
  }, [list, tab]);

  const groupedByDiet = useMemo(() => {
    const map = new Map<
      string,
      {
        dietId: string;
        title: string;
        meta: string;
        items: AdminApplication[];
        sortKey: string;
      }
    >();

    for (const a of visible) {
      const id = a.diet?.id || a.logbook_diet_id || "unknown";
      const title = dietLabel(a);
      const meta = dietMeta(a);

      const sortKey =
        a.diet?.start_date || a.diet?.created_at || a.created_at || "";

      if (!map.has(id)) {
        map.set(id, { dietId: id, title, meta, items: [], sortKey });
      }
      map.get(id)!.items.push(a);
    }

    return Array.from(map.values()).sort((x, y) =>
      String(y.sortKey || "").localeCompare(String(x.sortKey || ""))
    );
  }, [visible]);

  const clearAlerts = () => {
    setLocalError(null);
    setLocalSuccess(null);
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-sm text-gray-600">
            <Link to="/admin/dashboard" className="underline">
              Dashboard
            </Link>{" "}
            /{" "}
            <Link
              to={`/admin/applications/diets/${dietId}/categories`}
              state={{ dietId, dietTitle }}
              className="underline"
            >
              Categories
            </Link>{" "}
            / Category Applications
          </div>

          <h1 className="leading-tight text-2xl font-bold">
            {categoryName} Applications
          </h1>

          <p className="text-sm text-gray-600">
            Diet: <span className="font-medium">{dietTitle}</span> • Category:{" "}
            <span className="font-medium">{categoryName}</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 rounded-md border text-sm disabled:opacity-60"
            disabled={busy}
          >
            Back
          </button>

          <button
            onClick={() => setShowAnalysis(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-semibold bg-white hover:bg-indigo-50 hover:border-indigo-300 text-indigo-700 transition-colors disabled:opacity-60"
            disabled={busy}
          >
            <BarChart2 className="w-4 h-4" />
            Analysis
          </button>

          <button
            onClick={async () => {
              clearAlerts();
              try {
                await refetch();
                setLocalSuccess("Refreshed.");
                window.setTimeout(() => setLocalSuccess(null), 600);
              } catch (e: any) {
                setLocalError(safeMsg(e));
              }
            }}
            className="px-3 py-2 rounded-md border text-sm disabled:opacity-60"
            disabled={busy}
          >
            {busy ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {localSuccess && (
        <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
          {localSuccess}
        </div>
      )}

      {localError && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          {localError}
        </div>
      )}

      {error && !localError && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          {safeMsg(error)}
        </div>
      )}

      {/* ✅ Summary cards: ONLY the 3 + all */}
      {!busy && !error && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryCard
            label="Accepted"
            value={counts.approved}
            tone="approved"
          />
          <SummaryCard
            label="Submitted"
            value={counts.submitted}
            tone="submitted"
          />
          <SummaryCard
            label="Rejected"
            value={counts.rejected}
            tone="rejected"
          />
          <SummaryCard label="All" value={counts.all} tone="all" />
        </div>
      )}

      {/* ✅ Tabs: ONLY approved/submitted/rejected (+ all) */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => {
          const count =
            t.key === "approved"
              ? counts.approved
              : t.key === "submitted"
              ? counts.submitted
              : t.key === "rejected"
              ? counts.rejected
              : counts.all;

          const active = tab === t.key;

          return (
            <button
              key={t.key}
              onClick={() => {
                clearAlerts();
                setTab(t.key);
              }}
              className={[
                "px-3 py-2 text-sm transition",
                tabStyles(t.key, active),
              ].join(" ")}
              disabled={busy}
            >
              {t.label}
              <CountPill tab={t.key} count={count} />
            </button>
          );
        })}
      </div>

      {busy && <div className="text-sm">Loading…</div>}

      {!busy && visible.length === 0 && (
        <div className="p-4 rounded-md border bg-gray-50 text-sm text-gray-700">
          No applications found for <b>{tab}</b>.
        </div>
      )}

      {/* ✅ Grouped by diet */}
      {!busy && visible.length > 0 && (
        <div className="space-y-4">
          {groupedByDiet.map((g) => (
            <DietSection
              key={g.dietId}
              title={g.title}
              meta={g.meta}
              count={g.items.length}
            >
              {g.items.map((a) => (
                <CardRow key={a.id} a={a} />
              ))}
            </DietSection>
          ))}
        </div>
      )}

      {/* Category + diet analysis modal */}
      {showAnalysis && dietId && cid && (
        <ApplicationAnalysisReport
          scope={{
            kind: "category",
            dietId,
            categoryId: cid,
            dietLabel: dietTitle,
            categoryLabel: categoryName,
          }}
          onClose={() => setShowAnalysis(false)}
        />
      )}
    </div>
  );
}
